/**
 * MarketingEmailCampaignsClient
 *
 * Admin dashboard for Resend marketing email campaigns.
 *
 * Primary responsibilities:
 * - Create, edit, preview, send, and schedule marketing email campaigns
 * - Capture workspace and Resend audience/segment/topic targeting
 * - Grant campaign access to workspace users
 *
 * Dependencies:
 * - Admin marketing email campaign API
 * - Shared UI primitives
 */

'use client'

import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react'
import Link from 'next/link'
import {
  Bot,
  CalendarClock,
  CheckCircle2,
  Clock3,
  Eye,
  ImageIcon,
  ListFilter,
  Mail,
  Maximize2,
  MessageCircle,
  PanelRightOpen,
  Palette,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Send,
  ShieldCheck,
  Sparkles,
  Upload,
  UserPlus,
  Users,
  Wand2,
  X
} from 'lucide-react'
import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { Badge, StatusBadge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { ChatbotUIContext } from '@/context/context'
import { getContentSystemBrandProfiles } from '@/db/content-system'
import type { BrandProfile } from '@/lib/content-system/types'
import { useSplitPaneNavigation } from '@/components/splitview/split-pane-context'
import type { Tables } from '@/supabase/types'
import { supabase } from '@/lib/supabase/browser-client'
import {
  normalizeSocialUrl,
  normalizeWebsiteUrl,
  readUrlWithOptionalScheme
} from '@/lib/admin/marketing-email-template-links'
import { cn } from '@/lib/utils'
import { getFileUrl } from '@/lib/utils/get-file-url'

const API_BASE = '/api/admin/marketing-email-campaigns'
const RESEND_UNSUBSCRIBE_TOKEN = '{{{RESEND_UNSUBSCRIBE_URL}}}'
const TEMPLATE_CONTENT_START = '<!-- losi-template-content-start -->'
const TEMPLATE_CONTENT_END = '<!-- losi-template-content-end -->'
const EMPTY_PREVIEW_HTML =
  '<section style="font-family: Arial, sans-serif; padding: 32px; color: #333;"><h1>Email preview</h1><p>Write HTML content to preview this campaign.</p></section>'
const LOSI_USER_SYNC_BATCH_SIZE = 20

const CAMPAIGN_STATUSES = ['draft', 'scheduled', 'sending', 'sent', 'failed', 'cancelled'] as const

const PERMISSION_OPTIONS = [
  { value: 'view', label: 'Can view' },
  { value: 'edit', label: 'Can edit' },
  { value: 'send', label: 'Can send' },
  { value: 'owner', label: 'Owner' }
] as const

const TEMPLATE_STYLE_OPTIONS = [
  { value: 'clean', label: 'Clean' },
  { value: 'launch', label: 'Launch' },
  { value: 'newsletter', label: 'Newsletter' }
] as const

const BRAND_LOGO_FILE_TYPES = new Set([
  'avif',
  'gif',
  'jpeg',
  'jpg',
  'png',
  'webp'
])

type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number]
type CampaignPermission = (typeof PERMISSION_OPTIONS)[number]['value']
type CampaignTemplateStyle = (typeof TEMPLATE_STYLE_OPTIONS)[number]['value']
type ConfirmAction = 'send' | 'schedule'
type AiMode = 'draft' | 'edit' | 'subject' | 'rewrite'

type WorkspaceOption = {
  id: string
  name: string
}

type MarketingEmailCampaign = {
  createdAt: string | null
  fromEmail: string
  fromName: string
  html: string
  id: string
  name: string
  previewText: string
  replyTo: string
  resendAudienceId: string
  resendSegmentId: string
  resendTopicId: string
  scheduledAt: string | null
  sentAt: string | null
  status: CampaignStatus
  subject: string
  template: CampaignTemplateSettings
  text: string
  updatedAt: string | null
  workspaceId: string
}

type CampaignDraft = {
  fromEmail: string
  fromName: string
  html: string
  id: string | null
  name: string
  previewText: string
  replyTo: string
  resendAudienceId: string
  resendSegmentId: string
  resendTopicId: string
  scheduledAt: string
  subject: string
  template: CampaignTemplateSettings
  text: string
  workspaceId: string
}

type WorkspaceMember = {
  displayName: string | null
  email: string | null
  role: string
  userId: string
  username: string | null
}

type CampaignAccessGrant = {
  displayName: string | null
  email: string | null
  permission: CampaignPermission
  role: string | null
  userId: string
}

type CampaignPayload = {
  audience?: {
    resend_audience_id?: string
    resend_segment_id?: string
    resend_topic_id?: string
    segment?: {
      id: string
    }
  }
  from_email: string | null
  from_name: string | null
  html: string
  metadata?: Record<string, unknown>
  name: string
  preview_text: string | null
  reply_to: string | null
  subject: string
  text: string | null
  workspace_id: string
}

type CampaignAsset = {
  altText: string
  contentType: string
  createdAt: string | null
  id: string
  publicUrl: string
  sizeBytes: number
}

type BrandLogoOption = {
  id: string
  label: string
  source: 'Brand Kit' | 'Campaign images'
  url: string
}

type ResendSegmentOption = {
  createdAt: string | null
  id: string
  name: string
  source: string | null
  updatedAt: string | null
}

type BrandLogoFileRow = Pick<Tables<'files'>, 'file_path' | 'id' | 'name' | 'type'>

type CreateCampaignAssetInput = {
  altText: string
  file?: File
  sourceFileId?: string
}

type AiDraftPatch = {
  audience?: {
    resend_segment_id?: string
    resend_topic_id?: string
  }
  from_name?: string | null
  html?: string
  name?: string
  preview_text?: string | null
  reply_to?: string | null
  subject?: string
  text?: string | null
}

type CampaignTemplateSettings = {
  accentColor: string
  brandName: string
  footerAddress: string
  footerText: string
  headerTagline: string
  instagramUrl: string
  linkedinUrl: string
  logoUrl: string
  primaryColor: string
  style: CampaignTemplateStyle
  websiteUrl: string
  xUrl: string
}

const DEFAULT_TEMPLATE_SETTINGS: CampaignTemplateSettings = {
  accentColor: '#e11d48',
  brandName: 'Losi',
  footerAddress: '',
  footerText: 'You are receiving this because you signed up for updates from Losi.',
  headerTagline: 'Work, organized.',
  instagramUrl: '',
  linkedinUrl: '',
  logoUrl: '',
  primaryColor: '#111827',
  style: 'clean',
  websiteUrl: '',
  xUrl: ''
}

// --------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

// --------------

function readString(row: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = row[key]

    if (typeof value === 'string') {
      return value
    }
  }

  return ''
}

// --------------

function readNullableString(row: Record<string, unknown>, ...keys: string[]) {
  const value = readString(row, ...keys).trim()
  return value || null
}

// --------------

function readCampaignStatus(value: unknown): CampaignStatus {
  return typeof value === 'string' && CAMPAIGN_STATUSES.includes(value as CampaignStatus)
    ? (value as CampaignStatus)
    : 'draft'
}

// --------------

function readPermission(value: unknown): CampaignPermission {
  const permission = typeof value === 'string' ? value : ''
  const option = PERMISSION_OPTIONS.find((item) => item.value === permission)
  return option?.value ?? 'view'
}

// --------------

function parseCampaign(value: unknown): MarketingEmailCampaign | null {
  if (!isRecord(value)) {
    return null
  }

  const id = readString(value, 'id')

  if (!id) {
    return null
  }

  return {
    createdAt: readNullableString(value, 'created_at', 'createdAt'),
    fromEmail: readString(value, 'from_email', 'fromEmail'),
    fromName: readString(value, 'from_name', 'fromName'),
    html: readString(value, 'html', 'html_content', 'htmlContent'),
    id,
    name: readString(value, 'name') || 'Untitled campaign',
    previewText: readString(value, 'preview_text', 'previewText'),
    replyTo: readString(value, 'reply_to', 'replyTo'),
    resendAudienceId: readString(value, 'resend_audience_id', 'resendAudienceId', 'audience_id'),
    resendSegmentId: readString(value, 'resend_segment_id', 'resendSegmentId', 'segment_id'),
    resendTopicId: readString(value, 'resend_topic_id', 'resendTopicId', 'topic_id'),
    scheduledAt: readNullableString(value, 'scheduled_at', 'scheduledAt'),
    sentAt: readNullableString(value, 'sent_at', 'sentAt'),
    status: readCampaignStatus(value.status),
    subject: readString(value, 'subject'),
    template: readTemplateSettings(value.metadata),
    text: readString(value, 'text', 'text_content', 'textContent'),
    updatedAt: readNullableString(value, 'updated_at', 'updatedAt'),
    workspaceId: readString(value, 'workspace_id', 'workspaceId')
  }
}

// --------------

function readTemplateStyle(value: unknown): CampaignTemplateStyle {
  return typeof value === 'string' &&
    TEMPLATE_STYLE_OPTIONS.some((option) => option.value === value)
    ? (value as CampaignTemplateStyle)
    : DEFAULT_TEMPLATE_SETTINGS.style
}

// --------------

function readTemplateSettings(metadata: unknown): CampaignTemplateSettings {
  const metadataRecord = isRecord(metadata) ? metadata : {}
  const template = isRecord(metadataRecord.template) ? metadataRecord.template : {}

  return {
    accentColor:
      readString(template, 'accent_color', 'accentColor') || DEFAULT_TEMPLATE_SETTINGS.accentColor,
    brandName:
      readString(template, 'brand_name', 'brandName') || DEFAULT_TEMPLATE_SETTINGS.brandName,
    footerAddress: readString(template, 'footer_address', 'footerAddress'),
    footerText:
      readString(template, 'footer_text', 'footerText') || DEFAULT_TEMPLATE_SETTINGS.footerText,
    headerTagline:
      readString(template, 'header_tagline', 'headerTagline') ||
      DEFAULT_TEMPLATE_SETTINGS.headerTagline,
    instagramUrl: readString(template, 'instagram_url', 'instagramUrl'),
    linkedinUrl: readString(template, 'linkedin_url', 'linkedinUrl'),
    logoUrl: readString(template, 'logo_url', 'logoUrl'),
    primaryColor:
      readString(template, 'primary_color', 'primaryColor') ||
      DEFAULT_TEMPLATE_SETTINGS.primaryColor,
    style: readTemplateStyle(template.style),
    websiteUrl: readString(template, 'website_url', 'websiteUrl'),
    xUrl: readString(template, 'x_url', 'xUrl')
  }
}

// --------------

function parseCampaigns(value: unknown) {
  const rawCampaigns = Array.isArray(value)
    ? value
    : isRecord(value) && Array.isArray(value.campaigns)
      ? value.campaigns
      : isRecord(value) && Array.isArray(value.data)
        ? value.data
        : []

  return rawCampaigns
    .map(parseCampaign)
    .filter((campaign): campaign is MarketingEmailCampaign => Boolean(campaign))
}

// --------------

function parseCampaignResponse(value: unknown) {
  if (isRecord(value)) {
    return parseCampaign(value.campaign) || parseCampaign(value.data)
  }

  return parseCampaign(value)
}

// --------------

function parseMembers(value: unknown) {
  const rawMembers =
    isRecord(value) && Array.isArray(value.members)
      ? value.members
      : isRecord(value) && Array.isArray(value.users)
        ? value.users
        : []

  return rawMembers.reduce<WorkspaceMember[]>((members, item) => {
    if (!isRecord(item)) {
      return members
    }

    const userId = readString(item, 'userId', 'user_id', 'id')

    if (!userId) {
      return members
    }

    members.push({
      displayName: readNullableString(item, 'displayName', 'display_name', 'name'),
      email: readNullableString(item, 'email'),
      role: readString(item, 'role') || 'member',
      userId,
      username: readNullableString(item, 'username')
    })

    return members
  }, [])
}

// --------------

function parseGrants(value: unknown) {
  const rawGrants =
    isRecord(value) && Array.isArray(value.grants)
      ? value.grants
      : isRecord(value) && Array.isArray(value.access)
        ? value.access
        : []

  return rawGrants.reduce<CampaignAccessGrant[]>((grants, item) => {
    if (!isRecord(item)) {
      return grants
    }

    const userId = readString(item, 'userId', 'user_id')

    if (!userId) {
      return grants
    }

    grants.push({
      displayName: readNullableString(item, 'displayName', 'display_name', 'name'),
      email: readNullableString(item, 'email'),
      permission: readPermission(item.permission ?? item.access_level),
      role: readNullableString(item, 'role'),
      userId
    })

    return grants
  }, [])
}

// --------------

function readNumber(row: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = row[key]

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value
    }
  }

  return 0
}

// --------------

function parseAssets(value: unknown) {
  const rawAssets = isRecord(value) && Array.isArray(value.assets) ? value.assets : []

  return rawAssets.reduce<CampaignAsset[]>((assets, item) => {
    if (!isRecord(item)) return assets

    const id = readString(item, 'id')
    const publicUrl = readString(item, 'public_url', 'publicUrl')

    if (!id || !publicUrl) return assets

    assets.push({
      altText: readString(item, 'alt_text', 'altText'),
      contentType: readString(item, 'content_type', 'contentType'),
      createdAt: readNullableString(item, 'created_at', 'createdAt'),
      id,
      publicUrl,
      sizeBytes: readNumber(item, 'size_bytes', 'sizeBytes')
    })

    return assets
  }, [])
}

// --------------

function parseResendSegments(value: unknown) {
  const rawSegments = isRecord(value) && Array.isArray(value.segments) ? value.segments : []

  return rawSegments.reduce<ResendSegmentOption[]>((segments, item) => {
    if (!isRecord(item)) return segments

    const id = readString(item, 'id')

    if (!id) return segments

    segments.push({
      createdAt: readNullableString(item, 'created_at', 'createdAt'),
      id,
      name: readString(item, 'name') || id,
      source: readNullableString(item, 'source'),
      updatedAt: readNullableString(item, 'updated_at', 'updatedAt')
    })

    return segments
  }, [])
}

// --------------

function getResendSegmentLabel(segment: ResendSegmentOption) {
  return segment.name && segment.name !== segment.id
    ? `${segment.name} (${segment.id})`
    : segment.id
}

// --------------

function readStoredFileType(file: BrandLogoFileRow) {
  const storedType = file.type?.toLowerCase().trim()

  if (storedType) {
    return storedType.includes('/') ? storedType.split('/').pop() || storedType : storedType
  }

  return file.name.split('.').pop()?.toLowerCase() || ''
}

// --------------

function isBrandLogoFile(file: BrandLogoFileRow | null): file is BrandLogoFileRow {
  if (!file) {
    return false
  }

  const storedType = file.type?.toLowerCase().trim() || ''

  if (storedType.startsWith('image/')) {
    return true
  }

  return BRAND_LOGO_FILE_TYPES.has(readStoredFileType(file))
}

// --------------

async function resolveBrandLogoUrl(file: BrandLogoFileRow) {
  const filePath = file.file_path?.trim()

  if (!filePath) {
    return null
  }

  if (filePath.startsWith('{')) {
    try {
      const parsed = JSON.parse(filePath) as { url?: unknown }
      if (typeof parsed.url === 'string' && parsed.url) {
        return parsed.url
      }
    } catch {
      return null
    }
  }

  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath
  }

  try {
    return await getFileUrl(filePath)
  } catch {
    return null
  }
}

// --------------

function getBrandLogoFileIds(profiles: BrandProfile[]) {
  const activeProfile =
    profiles.find((profile) => profile.status === 'active') || profiles[0] || null

  return activeProfile?.brandLogoFileIds || []
}

// --------------

function dedupeLogoOptions(options: BrandLogoOption[]) {
  const urls = new Set<string>()

  return options.filter((option) => {
    if (urls.has(option.url)) {
      return false
    }

    urls.add(option.url)
    return true
  })
}

// --------------

async function getWorkspaceBrandLogoFile(fileId: string, workspaceId: string) {
  const { data, error } = await supabase
    .from('files')
    .select('id, name, type, file_path, file_workspaces!inner(workspace_id)')
    .eq('id', fileId)
    .eq('file_workspaces.workspace_id', workspaceId)
    .maybeSingle()

  if (error || !data) {
    return null
  }

  return data as BrandLogoFileRow
}

// --------------

function readOptionalNullableString(row: Record<string, unknown>, key: string) {
  if (!Object.prototype.hasOwnProperty.call(row, key)) {
    return undefined
  }

  return row[key] === null ? null : readNullableString(row, key)
}

// --------------

function parseAiDraftPatch(value: unknown): AiDraftPatch | null {
  if (!isRecord(value)) return null

  const patch = isRecord(value.draft_patch) ? value.draft_patch : value
  const audience = isRecord(patch.audience) ? patch.audience : undefined

  return {
    audience: audience
      ? {
          resend_segment_id: readString(audience, 'resend_segment_id') || undefined,
          resend_topic_id: readString(audience, 'resend_topic_id') || undefined
        }
      : undefined,
    from_name: readOptionalNullableString(patch, 'from_name'),
    html: readString(patch, 'html') || undefined,
    name: readString(patch, 'name') || undefined,
    preview_text: readOptionalNullableString(patch, 'preview_text'),
    reply_to: readOptionalNullableString(patch, 'reply_to'),
    subject: readString(patch, 'subject') || undefined,
    text: readOptionalNullableString(patch, 'text')
  }
}

// --------------

function getErrorMessage(payload: unknown, fallback: string) {
  if (isRecord(payload)) {
    const message = readString(payload, 'error', 'message')

    if (message) {
      return message
    }
  }

  return fallback
}

// --------------

async function requestJson(path: string, init: RequestInit = {}) {
  const headers = new Headers(init.headers)
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData

  if (init.body && !isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const response = await fetch(path, {
    ...init,
    cache: 'no-store',
    credentials: 'include',
    headers
  })
  const text = await response.text()
  let payload: unknown = {}

  if (text) {
    try {
      payload = JSON.parse(text) as unknown
    } catch {
      payload = { message: text }
    }
  }

  if (!response.ok) {
    throw new Error(getErrorMessage(payload, `Request failed with ${response.status}`))
  }

  return payload
}

// --------------

function createBlankDraft(workspaceId: string): CampaignDraft {
  return {
    fromEmail: '',
    fromName: '',
    html: '',
    id: null,
    name: '',
    previewText: '',
    replyTo: '',
    resendAudienceId: '',
    resendSegmentId: '',
    resendTopicId: '',
    scheduledAt: '',
    subject: '',
    template: DEFAULT_TEMPLATE_SETTINGS,
    text: '',
    workspaceId
  }
}

// --------------

function toDateTimeLocal(value: string | null) {
  if (!value) {
    return ''
  }

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return ''
  }

  const offsetMs = date.getTimezoneOffset() * 60_000
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16)
}

// --------------

function campaignToDraft(campaign: MarketingEmailCampaign): CampaignDraft {
  return {
    fromEmail: campaign.fromEmail,
    fromName: campaign.fromName,
    html: campaign.html,
    id: campaign.id,
    name: campaign.name,
    previewText: campaign.previewText,
    replyTo: campaign.replyTo,
    resendAudienceId: campaign.resendAudienceId,
    resendSegmentId: campaign.resendSegmentId,
    resendTopicId: campaign.resendTopicId,
    scheduledAt: toDateTimeLocal(campaign.scheduledAt),
    subject: campaign.subject,
    template: campaign.template,
    text: campaign.text,
    workspaceId: campaign.workspaceId
  }
}

// --------------

function toIsoOrNull(value: string) {
  if (!value.trim()) {
    return null
  }

  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

// --------------

function trimOrNull(value: string) {
  const trimmed = value.trim()
  return trimmed || null
}

// --------------

function draftToPayload(draft: CampaignDraft): CampaignPayload {
  const resendAudienceId = trimOrNull(draft.resendAudienceId) ?? undefined
  const resendSegmentId = trimOrNull(draft.resendSegmentId) ?? undefined
  const resendTopicId = trimOrNull(draft.resendTopicId) ?? undefined
  const audience =
    resendAudienceId || resendSegmentId || resendTopicId
      ? {
          ...(resendAudienceId ? { resend_audience_id: resendAudienceId } : {}),
          ...(resendSegmentId
            ? { resend_segment_id: resendSegmentId, segment: { id: resendSegmentId } }
            : {}),
          ...(resendTopicId ? { resend_topic_id: resendTopicId } : {})
        }
      : undefined

  return {
    audience,
    from_email: trimOrNull(draft.fromEmail),
    from_name: trimOrNull(draft.fromName),
    html: draft.html,
    metadata: {
      template: templateToMetadata(draft.template)
    },
    name: draft.name.trim(),
    preview_text: trimOrNull(draft.previewText),
    reply_to: trimOrNull(draft.replyTo),
    subject: draft.subject.trim(),
    text: trimOrNull(draft.text),
    workspace_id: draft.workspaceId
  }
}

// --------------

function draftToPatchPayload(draft: CampaignDraft): Omit<CampaignPayload, 'workspace_id'> {
  const payload = draftToPayload(draft)

  return {
    audience: payload.audience,
    from_email: payload.from_email,
    from_name: payload.from_name,
    html: payload.html,
    metadata: payload.metadata,
    name: payload.name,
    preview_text: payload.preview_text,
    reply_to: payload.reply_to,
    subject: payload.subject,
    text: payload.text
  }
}

// --------------

function draftToAiCampaign(draft: CampaignDraft) {
  return {
    from_email: trimOrNull(draft.fromEmail),
    from_name: trimOrNull(draft.fromName),
    html: draft.html,
    name: draft.name,
    preview_text: trimOrNull(draft.previewText),
    reply_to: trimOrNull(draft.replyTo),
    resend_segment_id: trimOrNull(draft.resendSegmentId),
    resend_topic_id: trimOrNull(draft.resendTopicId),
    subject: draft.subject,
    text: trimOrNull(draft.text)
  }
}

// --------------

function templateToMetadata(template: CampaignTemplateSettings) {
  const linksTemplate = normalizeTemplateLinks(template)

  return {
    accent_color: template.accentColor,
    brand_name: template.brandName,
    footer_address: template.footerAddress,
    footer_text: template.footerText,
    header_tagline: template.headerTagline,
    instagram_url: linksTemplate.instagramUrl,
    linkedin_url: linksTemplate.linkedinUrl,
    logo_url: readUrlWithOptionalScheme(template.logoUrl),
    primary_color: template.primaryColor,
    style: template.style,
    website_url: linksTemplate.websiteUrl,
    x_url: linksTemplate.xUrl
  }
}

// --------------

function templateToAiContext(template: CampaignTemplateSettings) {
  return {
    ...templateToMetadata(template),
    requirements: [
      'Use this visual system consistently across the campaign.',
      'Include a header with brand/logo when available.',
      'Include a footer with social links, address, and unsubscribe link.'
    ]
  }
}

// --------------

function escapeHtmlAttribute(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

// --------------

function escapeHtmlText(value: string) {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

// --------------

function normalizeTemplateLinks(template: CampaignTemplateSettings): CampaignTemplateSettings {
  return {
    ...template,
    instagramUrl: normalizeSocialUrl('instagram', template.instagramUrl),
    linkedinUrl: normalizeSocialUrl('linkedin', template.linkedinUrl),
    websiteUrl: normalizeWebsiteUrl(template.websiteUrl),
    xUrl: normalizeSocialUrl('x', template.xUrl)
  }
}

// --------------

function normalizeColor(value: string, fallback: string) {
  return /^#[0-9a-f]{6}$/i.test(value.trim()) ? value.trim() : fallback
}

// --------------

function buildSocialLinks(template: CampaignTemplateSettings) {
  const linksTemplate = normalizeTemplateLinks(template)
  const links = [
    { label: 'Website', url: linksTemplate.websiteUrl },
    { label: 'LinkedIn', url: linksTemplate.linkedinUrl },
    { label: 'Instagram', url: linksTemplate.instagramUrl },
    { label: 'X', url: linksTemplate.xUrl }
  ].filter((item) => item.url)

  if (links.length === 0) {
    return ''
  }

  return links
    .map(
      (link) =>
        `<a href="${escapeHtmlAttribute(link.url)}" style="color:#6b7280;text-decoration:none;margin:0 8px;">${escapeHtmlText(link.label)}</a>`
    )
    .join('')
}

// --------------

function extractTemplateContent(html: string) {
  const start = html.indexOf(TEMPLATE_CONTENT_START)
  const end = html.indexOf(TEMPLATE_CONTENT_END)

  if (start >= 0 && end > start) {
    return html.slice(start + TEMPLATE_CONTENT_START.length, end).trim()
  }

  return html.trim()
}

// --------------

function buildTemplateHtml(draft: CampaignDraft) {
  const template = draft.template
  const primaryColor = normalizeColor(template.primaryColor, DEFAULT_TEMPLATE_SETTINGS.primaryColor)
  const accentColor = normalizeColor(template.accentColor, DEFAULT_TEMPLATE_SETTINGS.accentColor)
  const logoUrl = readUrlWithOptionalScheme(template.logoUrl)
  const brandName = escapeHtmlText(template.brandName || DEFAULT_TEMPLATE_SETTINGS.brandName)
  const headerTagline = escapeHtmlText(template.headerTagline)
  const footerText = escapeHtmlText(template.footerText || DEFAULT_TEMPLATE_SETTINGS.footerText)
  const footerAddress = escapeHtmlText(template.footerAddress)
  const socialLinks = buildSocialLinks(template)
  const currentContent = extractTemplateContent(draft.html)
  const content = currentContent
    ? currentContent
    : `<h1 style="margin:0 0 16px;color:${primaryColor};font-size:28px;line-height:1.2;">${escapeHtmlText(draft.subject || draft.name || 'Campaign update')}</h1><p style="margin:0 0 20px;color:#4b5563;font-size:16px;line-height:1.6;">${escapeHtmlText(draft.previewText || 'Write the main campaign message here.')}</p>`
  const backgroundColor = template.style === 'launch' ? '#111827' : '#f3f4f6'
  const cardRadius = template.style === 'newsletter' ? '0' : '18px'

  return `<!doctype html>
<html>
  <body style="margin:0;padding:0;background:${backgroundColor};font-family:Arial,Helvetica,sans-serif;">
    <span style="display:none!important;opacity:0;color:transparent;height:0;width:0;overflow:hidden;">${escapeHtmlText(draft.previewText)}</span>
    <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background:${backgroundColor};padding:28px 14px;">
      <tr>
        <td align="center">
          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width:640px;background:#ffffff;border-radius:${cardRadius};overflow:hidden;">
            <tr>
              <td style="padding:28px 32px 18px;border-bottom:1px solid #e5e7eb;">
                ${logoUrl ? `<img src="${escapeHtmlAttribute(logoUrl)}" alt="${brandName}" width="128" style="display:block;max-width:128px;height:auto;border:0;margin:0 0 16px;" />` : `<div style="font-size:20px;font-weight:700;color:${primaryColor};margin-bottom:8px;">${brandName}</div>`}
                ${headerTagline ? `<div style="font-size:13px;letter-spacing:0.08em;text-transform:uppercase;color:${accentColor};font-weight:700;">${headerTagline}</div>` : ''}
              </td>
            </tr>
            <tr>
              <td style="padding:32px;color:#111827;font-size:16px;line-height:1.6;">
                ${TEMPLATE_CONTENT_START}
                ${content}
                ${TEMPLATE_CONTENT_END}
              </td>
            </tr>
            <tr>
              <td style="padding:24px 32px;background:#f9fafb;border-top:1px solid #e5e7eb;text-align:center;">
                <p style="margin:0 0 12px;color:#6b7280;font-size:12px;line-height:1.5;">${footerText}</p>
                ${footerAddress ? `<p style="margin:0 0 12px;color:#9ca3af;font-size:12px;line-height:1.5;">${footerAddress}</p>` : ''}
                ${socialLinks ? `<p style="margin:0 0 12px;font-size:12px;line-height:1.5;">${socialLinks}</p>` : ''}
                <p style="margin:0;color:#9ca3af;font-size:12px;line-height:1.5;"><a href="${RESEND_UNSUBSCRIBE_TOKEN}" style="color:#6b7280;text-decoration:underline;">Unsubscribe</a></p>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
</html>`
}

// --------------

function buildImageSnippet(asset: CampaignAsset) {
  return `<img src="${escapeHtmlAttribute(asset.publicUrl)}" alt="${escapeHtmlAttribute(asset.altText)}" width="600" style="display:block;max-width:100%;height:auto;border:0;" />`
}

// --------------

function formatBytes(value: number) {
  if (!value) return ''
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`

  return `${(value / (1024 * 1024)).toFixed(1)} MB`
}

// --------------

function buildPreviewDoc(html: string) {
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><base target="_blank"><style>body{margin:0;background:#fff;}img{max-width:100%;height:auto;}</style></head><body>${html.trim() || EMPTY_PREVIEW_HTML}</body></html>`
}

// --------------

function formatDate(value: string | null) {
  if (!value) {
    return 'Not set'
  }

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return value
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date)
}

// --------------

function getStatusTone(status: CampaignStatus) {
  if (status === 'sent') return 'success'
  if (status === 'scheduled' || status === 'sending') return 'info'
  if (status === 'failed' || status === 'cancelled') return 'danger'
  return 'neutral'
}

// --------------

function getMemberLabel(member: WorkspaceMember | CampaignAccessGrant) {
  return member.email || member.displayName || member.userId
}

// --------------

function getPermissionLabel(permission: CampaignPermission) {
  return PERMISSION_OPTIONS.find((option) => option.value === permission)?.label ?? 'Can view'
}

// --------------

function getActionCopy(action: ConfirmAction) {
  return action === 'send'
    ? {
        button: 'Send now',
        description: 'This will immediately hand the saved campaign to Resend.',
        title: 'Send this marketing email now?'
      }
    : {
        button: 'Schedule send',
        description: 'This will schedule the saved campaign for the selected time.',
        title: 'Schedule this marketing email?'
      }
}

// --------------

function createIdempotencyKey(action: ConfirmAction, campaignId: string) {
  return `${action}:${campaignId}:${Date.now()}`
}

export function MarketingEmailCampaignsClient({
  dashboardHref = '/marketing-emails',
  paneMode = false,
  showDashboardLink = false,
  showSplitPaneAction = false,
  showSidekick = true
}: {
  dashboardHref?: string
  paneMode?: boolean
  showDashboardLink?: boolean
  showSplitPaneAction?: boolean
  showSidekick?: boolean
}) {
  const { selectedWorkspace, workspaces } = useContext(ChatbotUIContext)
  const { openHrefInSecondaryPane } = useSplitPaneNavigation()
  const htmlTextareaRef = useRef<HTMLTextAreaElement | null>(null)
  const assetInputRef = useRef<HTMLInputElement | null>(null)
  const logoInputRef = useRef<HTMLInputElement | null>(null)
  const workspaceOptions = useMemo<WorkspaceOption[]>(() => {
    return workspaces.map((workspace) => ({
      id: workspace.id,
      name: workspace.name || 'Untitled workspace'
    }))
  }, [workspaces])

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(
    selectedWorkspace?.id || workspaceOptions[0]?.id || ''
  )
  const [campaigns, setCampaigns] = useState<MarketingEmailCampaign[]>([])
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null)
  const [draft, setDraft] = useState<CampaignDraft>(() => createBlankDraft(selectedWorkspaceId))
  const [isDirty, setIsDirty] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [actionLoading, setActionLoading] = useState<ConfirmAction | null>(null)
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null)
  const [members, setMembers] = useState<WorkspaceMember[]>([])
  const [grants, setGrants] = useState<CampaignAccessGrant[]>([])
  const [grantUserId, setGrantUserId] = useState('')
  const [grantPermission, setGrantPermission] = useState<CampaignPermission>('view')
  const [isAccessLoading, setIsAccessLoading] = useState(false)
  const [isGranting, setIsGranting] = useState(false)
  const [accessError, setAccessError] = useState<string | null>(null)
  const [assets, setAssets] = useState<CampaignAsset[]>([])
  const [assetAltText, setAssetAltText] = useState('')
  const [brandLogoOptions, setBrandLogoOptions] = useState<BrandLogoOption[]>([])
  const [resendSegments, setResendSegments] = useState<ResendSegmentOption[]>([])
  const [isAssetsLoading, setIsAssetsLoading] = useState(false)
  const [isSegmentsLoading, setIsSegmentsLoading] = useState(false)
  const [isUploadingAsset, setIsUploadingAsset] = useState(false)
  const [isUploadingLogo, setIsUploadingLogo] = useState(false)
  const [selectingLogoId, setSelectingLogoId] = useState<string | null>(null)
  const [isSyncingUsers, setIsSyncingUsers] = useState(false)
  const [isSidekickOpen, setIsSidekickOpen] = useState(false)
  const [sidekickInstruction, setSidekickInstruction] = useState('')
  const [sidekickMode, setSidekickMode] = useState<AiMode>('edit')
  const [isAiWorking, setIsAiWorking] = useState(false)

  const selectedCampaign = useMemo(() => {
    return campaigns.find((campaign) => campaign.id === selectedCampaignId) ?? null
  }, [campaigns, selectedCampaignId])

  const previewDoc = useMemo(() => buildPreviewDoc(draft.html), [draft.html])

  const campaignStats = useMemo(() => {
    return {
      drafts: campaigns.filter((campaign) => campaign.status === 'draft').length,
      scheduled: campaigns.filter((campaign) => campaign.status === 'scheduled').length,
      sent: campaigns.filter((campaign) => campaign.status === 'sent').length,
      total: campaigns.length
    }
  }, [campaigns])

  const availableMembers = useMemo(() => {
    const grantedUserIds = new Set(grants.map((grant) => grant.userId))
    return members.filter((member) => !grantedUserIds.has(member.userId))
  }, [grants, members])

  const confirmCopy = confirmAction ? getActionCopy(confirmAction) : null
  const campaignLogoOptions = useMemo<BrandLogoOption[]>(() => {
    return assets
      .filter((asset) => asset.publicUrl && asset.contentType.startsWith('image/'))
      .map((asset) => ({
        id: asset.id,
        label: asset.altText || 'Campaign image',
        source: 'Campaign images' as const,
        url: asset.publicUrl
      }))
  }, [assets])
  const logoOptions = useMemo(
    () => dedupeLogoOptions([...brandLogoOptions, ...campaignLogoOptions]),
    [brandLogoOptions, campaignLogoOptions]
  )
  const selectedLogoUrl = readUrlWithOptionalScheme(draft.template.logoUrl)
  const selectedResendSegment = useMemo(() => {
    return resendSegments.find((segment) => segment.id === draft.resendSegmentId) ?? null
  }, [draft.resendSegmentId, resendSegments])
  const normalizedTemplateLinks = useMemo(
    () => normalizeTemplateLinks(draft.template),
    [draft.template]
  )

  const loadCampaigns = useCallback(async () => {
    if (!selectedWorkspaceId) {
      setCampaigns([])
      setSelectedCampaignId(null)
      return
    }

    setIsLoading(true)

    try {
      const params = new URLSearchParams({ workspace_id: selectedWorkspaceId })
      const payload = await requestJson(`${API_BASE}?${params.toString()}`)
      const nextCampaigns = parseCampaigns(payload)

      setCampaigns(nextCampaigns)
      setSelectedCampaignId((current) => {
        if (current && nextCampaigns.some((campaign) => campaign.id === current)) {
          return current
        }

        return nextCampaigns[0]?.id ?? null
      })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load campaigns'
      toast.error(message)
      setCampaigns([])
      setSelectedCampaignId(null)
    } finally {
      setIsLoading(false)
    }
  }, [selectedWorkspaceId])

  const loadAccess = useCallback(async () => {
    if (!selectedCampaignId || !selectedWorkspaceId) {
      setMembers([])
      setGrants([])
      return
    }

    setIsAccessLoading(true)
    setAccessError(null)

    try {
      const params = new URLSearchParams({ workspace_id: selectedWorkspaceId })
      const payload = await requestJson(
        `${API_BASE}/${encodeURIComponent(selectedCampaignId)}/access?${params.toString()}`
      )

      setMembers(parseMembers(payload))
      setGrants(parseGrants(payload))
    } catch (error) {
      setMembers([])
      setGrants([])
      setAccessError(error instanceof Error ? error.message : 'Failed to load campaign access')
    } finally {
      setIsAccessLoading(false)
    }
  }, [selectedCampaignId, selectedWorkspaceId])

  const loadAssets = useCallback(async () => {
    if (!selectedWorkspaceId) {
      setAssets([])
      return
    }

    setIsAssetsLoading(true)

    try {
      const params = new URLSearchParams({ workspace_id: selectedWorkspaceId })

      if (draft.id) {
        params.set('campaign_id', draft.id)
      }

      const payload = await requestJson(`${API_BASE}/assets?${params.toString()}`)
      setAssets(parseAssets(payload))
    } catch (error) {
      setAssets([])
      toast.error(error instanceof Error ? error.message : 'Failed to load campaign images')
    } finally {
      setIsAssetsLoading(false)
    }
  }, [draft.id, selectedWorkspaceId])

  const loadResendSegments = useCallback(async () => {
    if (!selectedWorkspaceId) {
      setResendSegments([])
      return
    }

    setIsSegmentsLoading(true)

    try {
      const params = new URLSearchParams({ workspace_id: selectedWorkspaceId })
      const payload = await requestJson(`${API_BASE}/segments?${params.toString()}`)
      setResendSegments(parseResendSegments(payload))
    } catch (error) {
      setResendSegments([])
      toast.error(error instanceof Error ? error.message : 'Failed to load Resend segments')
    } finally {
      setIsSegmentsLoading(false)
    }
  }, [selectedWorkspaceId])

  useEffect(() => {
    if (selectedWorkspaceId || workspaceOptions.length === 0) {
      return
    }

    setSelectedWorkspaceId(selectedWorkspace?.id || workspaceOptions[0]?.id || '')
  }, [selectedWorkspace?.id, selectedWorkspaceId, workspaceOptions])

  useEffect(() => {
    void loadCampaigns()
  }, [loadCampaigns])

  useEffect(() => {
    if (selectedCampaign) {
      setDraft(campaignToDraft(selectedCampaign))
      setIsDirty(false)
      return
    }

    setDraft(createBlankDraft(selectedWorkspaceId))
    setIsDirty(false)
  }, [selectedCampaign, selectedWorkspaceId])

  useEffect(() => {
    void loadAccess()
  }, [loadAccess])

  useEffect(() => {
    void loadAssets()
  }, [loadAssets])

  useEffect(() => {
    void loadResendSegments()
  }, [loadResendSegments])

  useEffect(() => {
    if (!selectedWorkspaceId) {
      setBrandLogoOptions([])
      return
    }

    let isMounted = true
    const loadBrandLogos = async () => {
      try {
        const profiles = await getContentSystemBrandProfiles(selectedWorkspaceId)
        const logoFileIds = getBrandLogoFileIds(profiles)
        const options = (
          await Promise.all(
            logoFileIds.map(async (fileId, index) => {
              const file = await getWorkspaceBrandLogoFile(fileId, selectedWorkspaceId)

              if (!isBrandLogoFile(file)) {
                return null
              }

              const url = await resolveBrandLogoUrl(file)
              if (!url) {
                return null
              }

              return {
                id: file.id,
                label: file.name || `Brand logo ${index + 1}`,
                source: 'Brand Kit' as const,
                url
              }
            })
          )
        ).filter((option): option is BrandLogoOption => Boolean(option))

        if (isMounted) {
          setBrandLogoOptions(dedupeLogoOptions(options))
        }
      } catch {
        if (isMounted) {
          setBrandLogoOptions([])
        }
      }
    }

    void loadBrandLogos()

    return () => {
      isMounted = false
    }
  }, [selectedWorkspaceId])

  const updateDraft = <Key extends keyof CampaignDraft>(key: Key, value: CampaignDraft[Key]) => {
    setDraft((current) => ({
      ...current,
      [key]: value
    }))
    setIsDirty(true)
  }

  const updateTemplate = <Key extends keyof CampaignTemplateSettings>(
    key: Key,
    value: CampaignTemplateSettings[Key]
  ) => {
    setDraft((current) => ({
      ...current,
      template: {
        ...current.template,
        [key]: value
      }
    }))
    setIsDirty(true)
  }

  const generateTemplate = () => {
    setDraft((current) => ({
      ...current,
      html: buildTemplateHtml(current)
    }))
    setIsDirty(true)
    toast.success('Template generated')
  }

  const startNewCampaign = () => {
    setSelectedCampaignId(null)
    setDraft(createBlankDraft(selectedWorkspaceId))
    setIsDirty(false)
  }

  const saveCampaign = async () => {
    if (!draft.workspaceId) {
      toast.error('Select a workspace before saving')
      return null
    }

    if (!draft.name.trim() || !draft.subject.trim()) {
      toast.error('Campaign name and subject are required')
      return null
    }

    if (!draft.fromEmail.trim() || !draft.html.trim()) {
      toast.error('Sender email and HTML content are required')
      return null
    }

    setIsSaving(true)

    try {
      const path = draft.id ? `${API_BASE}/${encodeURIComponent(draft.id)}` : API_BASE
      const responsePayload = await requestJson(path, {
        body: JSON.stringify(draft.id ? draftToPatchPayload(draft) : draftToPayload(draft)),
        method: draft.id ? 'PATCH' : 'POST'
      })
      const savedCampaign = parseCampaignResponse(responsePayload)

      toast.success(draft.id ? 'Campaign updated' : 'Campaign created')
      await loadCampaigns()

      if (savedCampaign) {
        setSelectedCampaignId(savedCampaign.id)
        setDraft(campaignToDraft(savedCampaign))
      }

      setIsDirty(false)
      return savedCampaign
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save campaign'
      toast.error(message)
      return null
    } finally {
      setIsSaving(false)
    }
  }

  const saveCampaignBeforeAction = async () => {
    if (draft.id && !isDirty) {
      return draft
    }

    const scheduledAt = draft.scheduledAt
    const savedCampaign = await saveCampaign()

    if (!savedCampaign) {
      return null
    }

    const nextDraft = {
      ...campaignToDraft(savedCampaign),
      scheduledAt
    }

    setDraft(nextDraft)
    setIsDirty(false)

    return nextDraft
  }

  const openChatBeside = async () => {
    if (isDirty) {
      const savedCampaign = await saveCampaign()
      if (!savedCampaign) {
        return
      }
    }

    openHrefInSecondaryPane('/chat')
  }

  const openConfirmation = async (action: ConfirmAction) => {
    if (action === 'schedule' && !toIsoOrNull(draft.scheduledAt)) {
      toast.error('Choose a valid schedule time')
      return
    }

    const nextDraft = await saveCampaignBeforeAction()

    if (!nextDraft?.id) {
      return
    }

    if (!trimOrNull(nextDraft.resendSegmentId)) {
      toast.error('Choose a Resend segment before sending')
      return
    }

    setConfirmAction(action)
  }

  const runConfirmedAction = async () => {
    if (!confirmAction || !draft.id) {
      return
    }

    setActionLoading(confirmAction)

    try {
      const path = `${API_BASE}/${encodeURIComponent(draft.id)}/${confirmAction}`
      const scheduledAt = toIsoOrNull(draft.scheduledAt)

      if (confirmAction === 'schedule' && !scheduledAt) {
        throw new Error('Choose a valid schedule time')
      }

      const payload =
        confirmAction === 'schedule'
          ? {
              confirmation: 'SCHEDULE_MARKETING_EMAIL_CAMPAIGN',
              idempotency_key: createIdempotencyKey(confirmAction, draft.id),
              scheduled_at: scheduledAt
            }
          : {
              confirmation: 'SEND_MARKETING_EMAIL_CAMPAIGN',
              idempotency_key: createIdempotencyKey(confirmAction, draft.id)
            }
      const responsePayload = await requestJson(path, {
        body: JSON.stringify(payload),
        method: 'POST'
      })
      const updatedCampaign = parseCampaignResponse(responsePayload)

      toast.success(confirmAction === 'send' ? 'Campaign send started' : 'Campaign scheduled')
      await loadCampaigns()

      if (updatedCampaign) {
        setSelectedCampaignId(updatedCampaign.id)
      }

      setConfirmAction(null)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Campaign action failed'
      toast.error(message)
    } finally {
      setActionLoading(null)
    }
  }

  const grantAccess = async () => {
    if (!draft.id || !grantUserId) {
      toast.error('Choose a campaign and workspace user')
      return
    }

    setIsGranting(true)

    try {
      await requestJson(`${API_BASE}/${encodeURIComponent(draft.id)}/access`, {
        body: JSON.stringify({
          access_level: grantPermission,
          permission: grantPermission,
          user_id: grantUserId,
          workspace_id: draft.workspaceId
        }),
        method: 'POST'
      })

      toast.success('Campaign access granted')
      setGrantUserId('')
      setGrantPermission('view')
      await loadAccess()
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to grant access'
      toast.error(message)
    } finally {
      setIsGranting(false)
    }
  }

  const applyAiPatch = (patch: AiDraftPatch) => {
    setDraft((current) => ({
      ...current,
      fromName: patch.from_name === undefined ? current.fromName : (patch.from_name ?? ''),
      html: patch.html ?? current.html,
      name: patch.name ?? current.name,
      previewText:
        patch.preview_text === undefined ? current.previewText : (patch.preview_text ?? ''),
      replyTo: patch.reply_to === undefined ? current.replyTo : (patch.reply_to ?? ''),
      resendSegmentId: patch.audience?.resend_segment_id ?? current.resendSegmentId,
      resendTopicId: patch.audience?.resend_topic_id ?? current.resendTopicId,
      subject: patch.subject ?? current.subject,
      text: patch.text === undefined ? current.text : (patch.text ?? '')
    }))
    setIsDirty(true)
  }

  const runCampaignAi = async (mode: AiMode = sidekickMode) => {
    if (!selectedWorkspaceId) {
      toast.error('Select a workspace first')
      return
    }

    const instruction = sidekickInstruction.trim()
    if (!instruction) {
      toast.error('Tell the assistant what to change')
      return
    }

    setIsAiWorking(true)

    try {
      const payload = await requestJson(`${API_BASE}/ai`, {
        body: JSON.stringify({
          allowed_assets: assets.map((asset) => ({
            alt_text: asset.altText,
            id: asset.id,
            public_url: asset.publicUrl
          })),
          allowed_resend_segment_ids: [draft.resendSegmentId].filter(Boolean),
          campaign: draftToAiCampaign(draft),
          campaign_id: draft.id,
          instruction,
          mode,
          template_context: templateToAiContext(draft.template),
          workspace_id: selectedWorkspaceId
        }),
        method: 'POST'
      })
      const patch = parseAiDraftPatch(payload)

      if (!patch) {
        throw new Error('AI did not return a campaign edit')
      }

      applyAiPatch(patch)
      toast.success('Campaign draft updated')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'AI campaign edit failed')
    } finally {
      setIsAiWorking(false)
    }
  }

  const createCampaignAsset = async (input: CreateCampaignAssetInput) => {
    if (!selectedWorkspaceId) {
      throw new Error('Select a workspace first')
    }

    const formData = new FormData()
    formData.append('workspace_id', selectedWorkspaceId)

    if (input.file) {
      formData.append('file', input.file)
    } else if (input.sourceFileId) {
      formData.append('source_file_id', input.sourceFileId)
    }

    if (draft.id) {
      formData.append('campaign_id', draft.id)
    }

    if (input.altText.trim()) {
      formData.append('alt_text', input.altText.trim())
    }

    const payload = await requestJson(`${API_BASE}/assets`, {
      body: formData,
      method: 'POST'
    })
    const nextAssets = parseAssets({ assets: [isRecord(payload) ? payload.asset : null] })
    const uploadedAsset = nextAssets[0]

    if (!uploadedAsset) {
      throw new Error('Upload did not return an image asset')
    }

    setAssets((current) => [uploadedAsset, ...current])
    return uploadedAsset
  }

  const uploadMarketingAsset = async (file: File) => {
    setIsUploadingAsset(true)

    try {
      const uploadedAsset = await createCampaignAsset({
        altText: assetAltText,
        file
      })
      setAssetAltText('')
      insertAssetIntoHtml(uploadedAsset)
      toast.success('Image uploaded')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Image upload failed')
    } finally {
      setIsUploadingAsset(false)
      if (assetInputRef.current) {
        assetInputRef.current.value = ''
      }
    }
  }

  const uploadTemplateLogo = async (file: File) => {
    setIsUploadingLogo(true)

    try {
      const uploadedAsset = await createCampaignAsset({
        altText: `${draft.template.brandName || 'Brand'} logo`,
        file
      })

      updateTemplate('logoUrl', uploadedAsset.publicUrl)
      toast.success('Logo uploaded')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Logo upload failed')
    } finally {
      setIsUploadingLogo(false)
      if (logoInputRef.current) {
        logoInputRef.current.value = ''
      }
    }
  }

  const selectLogoOption = async (option: BrandLogoOption) => {
    if (option.source === 'Campaign images') {
      updateTemplate('logoUrl', option.url)
      return
    }

    setSelectingLogoId(option.id)

    try {
      const asset = await createCampaignAsset({
        altText: option.label,
        sourceFileId: option.id
      })

      updateTemplate('logoUrl', asset.publicUrl)
      toast.success('Brand Kit logo copied into campaign')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Brand Kit logo import failed')
    } finally {
      setSelectingLogoId(null)
    }
  }

  const insertAssetIntoHtml = (asset: CampaignAsset) => {
    const snippet = buildImageSnippet(asset)
    const textarea = htmlTextareaRef.current
    const start = textarea?.selectionStart ?? draft.html.length
    const end = textarea?.selectionEnd ?? start
    const nextHtml = `${draft.html.slice(0, start)}\n${snippet}\n${draft.html.slice(end)}`

    updateDraft('html', nextHtml)

    window.requestAnimationFrame(() => {
      const nextCursor = start + snippet.length + 2
      htmlTextareaRef.current?.focus()
      htmlTextareaRef.current?.setSelectionRange(nextCursor, nextCursor)
    })
  }

  const syncLosiUsersToResend = async () => {
    if (!draft.id) {
      toast.error('Save the campaign before syncing Losi users')
      return
    }

    const selectedResendSegmentId = trimOrNull(draft.resendSegmentId)

    if (!selectedResendSegmentId && resendSegments.length > 0) {
      toast.error('Choose an existing Resend segment before syncing Losi users')
      return
    }

    setIsSyncingUsers(true)

    try {
      let hasMore = true
      let nextOffset: number | undefined
      let segmentId = ''
      let synced = 0
      let skipped = 0
      let batches = 0

      while (hasMore && batches < 30) {
        const payload = await requestJson(`${API_BASE}/${encodeURIComponent(draft.id)}/sync`, {
          body: JSON.stringify({
            limit: LOSI_USER_SYNC_BATCH_SIZE,
            ...(selectedResendSegmentId ? { resend_segment_id: selectedResendSegmentId } : {}),
            ...(typeof nextOffset === 'number' ? { offset: nextOffset } : {})
          }),
          method: 'POST'
        })

        if (isRecord(payload)) {
          segmentId = readString(payload, 'resend_segment_id') || segmentId
          synced += readNumber(payload, 'synced')
          skipped += readNumber(payload, 'skipped')
          hasMore = payload.has_more === true
          const payloadNextOffset = readNumber(payload, 'next_offset')
          if (hasMore && payloadNextOffset <= (nextOffset ?? -1)) {
            throw new Error('Losi user sync did not advance')
          }
          nextOffset = payloadNextOffset
        } else {
          hasMore = false
        }

        batches += 1
      }

      if (segmentId) {
        updateDraft('resendSegmentId', segmentId)
      }

      toast.success(
        hasMore
          ? `Synced ${synced} Losi users. Run sync again to continue.`
          : `Synced ${synced} Losi users to Resend${skipped ? `, skipped ${skipped}` : ''}`
      )
      await loadCampaigns()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Losi user sync failed')
    } finally {
      setIsSyncingUsers(false)
    }
  }

  const twoColumnGridClass = cn('grid gap-3', paneMode ? 'grid-cols-1' : 'md:grid-cols-2')
  const threeColumnGridClass = cn('grid gap-3', paneMode ? 'grid-cols-1' : 'md:grid-cols-3')
  const socialGridClass = cn('grid gap-3', paneMode ? 'grid-cols-1' : 'md:grid-cols-4')
  const scheduleGridClass = cn(
    'grid gap-3 border-t pt-4',
    paneMode ? 'grid-cols-1' : 'md:grid-cols-[1fr_auto] md:items-end'
  )
  const scheduleActionsGridClass = cn(
    'grid gap-2',
    paneMode ? 'grid-cols-1' : 'sm:grid-cols-3 md:grid-cols-[auto_auto_auto]'
  )
  const compactActionGridClass = cn(
    'grid gap-2',
    paneMode ? 'grid-cols-1' : 'sm:grid-cols-[1fr_auto]'
  )
  const accessGrantGridClass = cn(
    'grid gap-2',
    paneMode ? 'grid-cols-1' : 'md:grid-cols-[1fr_140px_auto]'
  )

  return (
    <div
      className={cn(
        'mx-auto flex w-full flex-col gap-4 px-4 py-5 md:px-6',
        paneMode ? 'max-w-none p-3 md:px-4' : 'max-w-[1500px]'
      )}
    >
      <div
        className={cn(
          'flex flex-col gap-3 border-b pb-4',
          paneMode ? 'items-stretch' : 'lg:flex-row lg:items-center lg:justify-between'
        )}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Mail className="text-muted-foreground size-5" />
            <h1 className="truncate text-xl font-semibold tracking-tight">
              Marketing email campaigns
            </h1>
          </div>
          <p className="text-muted-foreground mt-1 max-w-3xl text-sm">
            Create Resend marketing campaigns, review the rendered email, and grant campaign access
            to workspace users.
          </p>
        </div>

        <div
          className={cn(
            'flex w-full flex-col gap-2',
            paneMode
              ? ''
              : 'sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end'
          )}
        >
          <div className={cn(paneMode ? 'w-full' : 'sm:min-w-[180px] sm:max-w-[320px] sm:flex-1')}>
            <Select
              value={selectedWorkspaceId}
              onValueChange={(value) => {
                setSelectedWorkspaceId(value)
                setSelectedCampaignId(null)
              }}
            >
              <SelectTrigger aria-label="Workspace">
                <SelectValue placeholder="Select workspace" />
              </SelectTrigger>
              <SelectContent>
                {workspaceOptions.map((workspace) => (
                  <SelectItem key={workspace.id} value={workspace.id}>
                    {workspace.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {showDashboardLink ? (
            <Button asChild variant="outline" className={cn(paneMode ? 'w-full' : 'shrink-0')}>
              <Link href={dashboardHref} aria-label="Open marketing campaign dashboard">
                <Maximize2 className="size-4" />
                Open dashboard
              </Link>
            </Button>
          ) : null}
          {showSplitPaneAction ? (
            <Button
              type="button"
              variant="outline"
              className={cn(paneMode ? 'w-full' : 'shrink-0')}
              onClick={() => void openChatBeside()}
            >
              <PanelRightOpen className="size-4" />
              Open chat beside
            </Button>
          ) : null}
          <Button
            variant="outline"
            className={cn(paneMode ? 'w-full' : 'shrink-0')}
            onClick={loadCampaigns}
            loading={isLoading}
          >
            <RefreshCw className="size-4" />
            Refresh
          </Button>
          <Button className={cn(paneMode ? 'w-full' : 'shrink-0')} onClick={startNewCampaign}>
            <Plus className="size-4" />
            New campaign
          </Button>
        </div>
      </div>

      <div className={cn('grid gap-3 sm:grid-cols-2', paneMode ? '' : 'lg:grid-cols-4')}>
        <Metric label="Campaigns" value={campaignStats.total} />
        <Metric label="Drafts" value={campaignStats.drafts} />
        <Metric label="Scheduled" value={campaignStats.scheduled} />
        <Metric label="Sent" value={campaignStats.sent} />
      </div>

      <div
        className={cn(
          'grid gap-4',
          paneMode ? 'grid-cols-1' : '2xl:grid-cols-[minmax(240px,0.8fr)_minmax(420px,1.25fr)_minmax(320px,1fr)]'
        )}
      >
        <Card className="min-h-[420px]">
          <CardHeader className="space-y-1 pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="text-muted-foreground size-4" />
              Campaigns
            </CardTitle>
            <CardDescription className="text-xs">
              {selectedWorkspaceId ? 'Workspace campaign drafts and sends.' : 'Select a workspace.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {campaigns.length === 0 ? (
              <div className="border-border/70 bg-muted/20 rounded-md border border-dashed p-4 text-sm">
                <p className="font-medium">No campaigns loaded</p>
                <p className="text-muted-foreground mt-1 text-xs">
                  Create a draft or refresh after the API is available.
                </p>
              </div>
            ) : (
              campaigns.map((campaign) => (
                <button
                  key={campaign.id}
                  type="button"
                  onClick={() => setSelectedCampaignId(campaign.id)}
                  className={cn(
                    'hover:bg-muted/50 flex w-full flex-col gap-2 rounded-md border p-3 text-left text-sm transition-colors',
                    selectedCampaignId === campaign.id
                      ? 'border-primary/50 bg-primary/5'
                      : 'border-border/70'
                  )}
                >
                  <div className="flex min-w-0 items-center justify-between gap-2">
                    <span className="truncate font-medium">{campaign.name}</span>
                    <StatusBadge tone={getStatusTone(campaign.status)} density="compact" dot>
                      {campaign.status}
                    </StatusBadge>
                  </div>
                  <p className="text-muted-foreground line-clamp-2 text-xs">
                    {campaign.subject || 'No subject yet'}
                  </p>
                  <div className="text-muted-foreground flex items-center gap-1 text-[11px]">
                    <Clock3 className="size-3" />
                    {campaign.scheduledAt
                      ? `Scheduled ${formatDate(campaign.scheduledAt)}`
                      : `Updated ${formatDate(campaign.updatedAt || campaign.createdAt)}`}
                  </div>
                </button>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="space-y-1 pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-base">
                {draft.id ? (
                  <Pencil className="text-muted-foreground size-4" />
                ) : (
                  <Plus className="text-muted-foreground size-4" />
                )}
                {draft.id ? 'Edit campaign' : 'Create campaign'}
              </CardTitle>
              <div className="flex items-center gap-2">
                {isDirty ? (
                  <Badge variant="warning">Unsaved</Badge>
                ) : (
                  <Badge variant="muted">Saved</Badge>
                )}
                {selectedCampaign ? (
                  <StatusBadge tone={getStatusTone(selectedCampaign.status)} density="compact" dot>
                    {selectedCampaign.status}
                  </StatusBadge>
                ) : null}
              </div>
            </div>
            <CardDescription className="text-xs">
              Save before sending so the confirmation uses the persisted campaign.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="space-y-4"
              onSubmit={(event) => {
                event.preventDefault()
                void saveCampaign()
              }}
            >
              <div className={twoColumnGridClass}>
                <Field label="Campaign name" htmlFor="campaign-name">
                  <Input
                    id="campaign-name"
                    value={draft.name}
                    onChange={(event) => updateDraft('name', event.target.value)}
                    placeholder="July customer reactivation"
                  />
                </Field>
                <Field label="Subject" htmlFor="campaign-subject">
                  <Input
                    id="campaign-subject"
                    value={draft.subject}
                    onChange={(event) => updateDraft('subject', event.target.value)}
                    placeholder="A short update from Losi"
                  />
                </Field>
              </div>

              <Field label="Preview text" htmlFor="campaign-preview-text">
                <Input
                  id="campaign-preview-text"
                  value={draft.previewText}
                  onChange={(event) => updateDraft('previewText', event.target.value)}
                  placeholder="Shown beside the subject in inboxes"
                />
              </Field>

              <div className={threeColumnGridClass}>
                <Field label="From name" htmlFor="campaign-from-name">
                  <Input
                    id="campaign-from-name"
                    value={draft.fromName}
                    onChange={(event) => updateDraft('fromName', event.target.value)}
                    placeholder="Losi"
                  />
                </Field>
                <Field label="From email" htmlFor="campaign-from-email">
                  <Input
                    id="campaign-from-email"
                    type="email"
                    value={draft.fromEmail}
                    onChange={(event) => updateDraft('fromEmail', event.target.value)}
                    placeholder="updates@email.losi.ai"
                  />
                </Field>
                <Field label="Reply-to" htmlFor="campaign-reply-to">
                  <Input
                    id="campaign-reply-to"
                    type="email"
                    value={draft.replyTo}
                    onChange={(event) => updateDraft('replyTo', event.target.value)}
                    placeholder="support@email.losi.ai"
                  />
                </Field>
              </div>

              <div className="space-y-3 rounded-md border p-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Palette className="text-muted-foreground size-4" />
                      <p className="text-sm font-medium">Template styling</p>
                    </div>
                    <p className="text-muted-foreground mt-1 text-xs">
                      Keep headers, logo, footer, socials, and unsubscribe links consistent.
                    </p>
                  </div>
                  <Button type="button" variant="outline" onClick={generateTemplate}>
                    <Wand2 className="size-4" />
                    Generate template
                  </Button>
                </div>

                <div className={threeColumnGridClass}>
                  <Field label="Template style" htmlFor="campaign-template-style">
                    <Select
                      value={draft.template.style}
                      onValueChange={(value) => updateTemplate('style', readTemplateStyle(value))}
                    >
                      <SelectTrigger id="campaign-template-style" aria-label="Template style">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TEMPLATE_STYLE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Brand name" htmlFor="campaign-template-brand">
                    <Input
                      id="campaign-template-brand"
                      value={draft.template.brandName}
                      onChange={(event) => updateTemplate('brandName', event.target.value)}
                      placeholder="Losi"
                    />
                  </Field>
                  <Field label="Header tagline" htmlFor="campaign-template-tagline">
                    <Input
                      id="campaign-template-tagline"
                      value={draft.template.headerTagline}
                      onChange={(event) => updateTemplate('headerTagline', event.target.value)}
                      placeholder="Work, organized."
                    />
                  </Field>
                </div>

                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0]
                    if (file) void uploadTemplateLogo(file)
                  }}
                />

                <div
                  className={cn(
                    'grid gap-3',
                    paneMode ? 'grid-cols-1' : 'lg:grid-cols-[minmax(260px,1fr)_220px]'
                  )}
                >
                  <div className="space-y-3">
                    <Field label="Logo" htmlFor="campaign-template-logo">
                      <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                        <Input
                          id="campaign-template-logo"
                          value={draft.template.logoUrl}
                          onChange={(event) => updateTemplate('logoUrl', event.target.value)}
                          placeholder="https://logo.png"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          loading={isUploadingLogo}
                          onClick={() => logoInputRef.current?.click()}
                        >
                          <Upload className="size-4" />
                          Upload logo
                        </Button>
                      </div>
                    </Field>

                    {logoOptions.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-muted-foreground text-xs font-medium">
                          Brand Kit logos and campaign images
                        </p>
                        <div className="grid max-h-44 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
                          {logoOptions.map((option) => (
                            <button
                              key={`${option.source}:${option.id}`}
                              type="button"
                              disabled={selectingLogoId === option.id}
                              onClick={() => void selectLogoOption(option)}
                              className={cn(
                                'hover:bg-muted/50 grid grid-cols-[40px_1fr] items-center gap-2 rounded-md border p-2 text-left text-xs transition-colors',
                                selectedLogoUrl === option.url
                                  ? 'border-primary/60 bg-primary/5'
                                  : 'border-border/70',
                                selectingLogoId === option.id && 'opacity-60'
                              )}
                            >
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img
                                src={option.url}
                                alt={option.label}
                                className="size-10 rounded border bg-white object-contain p-1"
                              />
                              <span className="min-w-0">
                                <span className="block truncate font-medium">
                                  {option.label}
                                </span>
                                <span className="text-muted-foreground block truncate">
                                  {option.source}
                                </span>
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="bg-muted/20 rounded-md border p-3">
                    <p className="text-muted-foreground text-xs font-medium">Selected logo</p>
                    {selectedLogoUrl ? (
                      <div className="mt-2 space-y-2">
                        <div className="flex h-24 items-center justify-center rounded-md border bg-white p-3">
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img
                            src={selectedLogoUrl}
                            alt={draft.template.brandName || 'Selected logo'}
                            className="max-h-full max-w-full object-contain"
                          />
                        </div>
                        <p className="text-muted-foreground truncate text-xs">
                          {selectedLogoUrl}
                        </p>
                      </div>
                    ) : (
                      <div className="text-muted-foreground mt-2 flex h-24 items-center justify-center rounded-md border border-dashed text-xs">
                        No logo selected
                      </div>
                    )}
                  </div>
                </div>

                <div className={twoColumnGridClass}>
                  <Field label="Primary" htmlFor="campaign-template-primary">
                    <Input
                      id="campaign-template-primary"
                      type="color"
                      value={normalizeColor(
                        draft.template.primaryColor,
                        DEFAULT_TEMPLATE_SETTINGS.primaryColor
                      )}
                      onChange={(event) => updateTemplate('primaryColor', event.target.value)}
                    />
                  </Field>
                  <Field label="Accent" htmlFor="campaign-template-accent">
                    <Input
                      id="campaign-template-accent"
                      type="color"
                      value={normalizeColor(
                        draft.template.accentColor,
                        DEFAULT_TEMPLATE_SETTINGS.accentColor
                      )}
                      onChange={(event) => updateTemplate('accentColor', event.target.value)}
                    />
                  </Field>
                </div>

                <div className={twoColumnGridClass}>
                  <Field label="Footer note" htmlFor="campaign-template-footer">
                    <Input
                      id="campaign-template-footer"
                      value={draft.template.footerText}
                      onChange={(event) => updateTemplate('footerText', event.target.value)}
                      placeholder="Why this user is receiving the email"
                    />
                  </Field>
                  <Field label="Business address" htmlFor="campaign-template-address">
                    <Input
                      id="campaign-template-address"
                      value={draft.template.footerAddress}
                      onChange={(event) => updateTemplate('footerAddress', event.target.value)}
                      placeholder="Address shown in the email footer"
                    />
                  </Field>
                </div>

                <div className={socialGridClass}>
                  <Field label="Website" htmlFor="campaign-template-website">
                    <Input
                      id="campaign-template-website"
                      value={draft.template.websiteUrl}
                      onChange={(event) => updateTemplate('websiteUrl', event.target.value)}
                      placeholder="losi.ai or https://losi.ai"
                    />
                    {normalizedTemplateLinks.websiteUrl ? (
                      <p className="text-muted-foreground truncate text-xs">
                        {normalizedTemplateLinks.websiteUrl}
                      </p>
                    ) : null}
                  </Field>
                  <Field label="LinkedIn" htmlFor="campaign-template-linkedin">
                    <Input
                      id="campaign-template-linkedin"
                      value={draft.template.linkedinUrl}
                      onChange={(event) => updateTemplate('linkedinUrl', event.target.value)}
                      placeholder="company/losi or full link"
                    />
                    {normalizedTemplateLinks.linkedinUrl ? (
                      <p className="text-muted-foreground truncate text-xs">
                        {normalizedTemplateLinks.linkedinUrl}
                      </p>
                    ) : null}
                  </Field>
                  <Field label="Instagram" htmlFor="campaign-template-instagram">
                    <Input
                      id="campaign-template-instagram"
                      value={draft.template.instagramUrl}
                      onChange={(event) => updateTemplate('instagramUrl', event.target.value)}
                      placeholder="@losi or full link"
                    />
                    {normalizedTemplateLinks.instagramUrl ? (
                      <p className="text-muted-foreground truncate text-xs">
                        {normalizedTemplateLinks.instagramUrl}
                      </p>
                    ) : null}
                  </Field>
                  <Field label="X" htmlFor="campaign-template-x">
                    <Input
                      id="campaign-template-x"
                      value={draft.template.xUrl}
                      onChange={(event) => updateTemplate('xUrl', event.target.value)}
                      placeholder="@losi or full link"
                    />
                    {normalizedTemplateLinks.xUrl ? (
                      <p className="text-muted-foreground truncate text-xs">
                        {normalizedTemplateLinks.xUrl}
                      </p>
                    ) : null}
                  </Field>
                </div>
              </div>

              <div className={threeColumnGridClass}>
                <Field label="Resend audience ID" htmlFor="resend-audience-id">
                  <Input
                    id="resend-audience-id"
                    value={draft.resendAudienceId}
                    onChange={(event) => updateDraft('resendAudienceId', event.target.value)}
                    placeholder="aud_..."
                  />
                </Field>
                <Field label="Resend segment ID" htmlFor="resend-segment-id">
                  <div className="space-y-2">
                    {resendSegments.length > 0 ? (
                      <Select
                        value={draft.resendSegmentId || undefined}
                        onValueChange={(value) => updateDraft('resendSegmentId', value)}
                      >
                        <SelectTrigger
                          id="resend-segment-select"
                          aria-label="Select existing Resend segment"
                        >
                          <SelectValue placeholder="Select existing Resend segment" />
                        </SelectTrigger>
                        <SelectContent>
                          {resendSegments.map((segment) => (
                            <SelectItem key={segment.id} value={segment.id}>
                              {getResendSegmentLabel(segment)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : null}
                    <Input
                      id="resend-segment-id"
                      value={draft.resendSegmentId}
                      onChange={(event) => updateDraft('resendSegmentId', event.target.value)}
                      placeholder={isSegmentsLoading ? 'Loading Resend segments...' : 'seg_...'}
                    />
                  </div>
                </Field>
                <Field label="Resend topic ID" htmlFor="resend-topic-id">
                  <Input
                    id="resend-topic-id"
                    value={draft.resendTopicId}
                    onChange={(event) => updateDraft('resendTopicId', event.target.value)}
                    placeholder="topic_..."
                  />
                </Field>
              </div>

              <Field label="HTML email" htmlFor="campaign-html">
                <Textarea
                  ref={htmlTextareaRef}
                  id="campaign-html"
                  value={draft.html}
                  onChange={(event) => updateDraft('html', event.target.value)}
                  className="min-h-[220px] font-mono text-xs"
                  placeholder="<main><h1>Hello</h1><p>Your campaign body...</p><p><a href='{{{RESEND_UNSUBSCRIBE_URL}}}'>Unsubscribe</a></p></main>"
                />
              </Field>

              <Field label="Plain text fallback" htmlFor="campaign-text">
                <Textarea
                  id="campaign-text"
                  value={draft.text}
                  onChange={(event) => updateDraft('text', event.target.value)}
                  className="min-h-[110px]"
                  placeholder="Plain text version for clients that do not render HTML."
                />
              </Field>

              <div className={scheduleGridClass}>
                <Field label="Schedule time" htmlFor="campaign-scheduled-at">
                  <Input
                    id="campaign-scheduled-at"
                    type="datetime-local"
                    value={draft.scheduledAt}
                    onChange={(event) => updateDraft('scheduledAt', event.target.value)}
                  />
                </Field>
                <div className={scheduleActionsGridClass}>
                  <Button type="submit" loading={isSaving}>
                    <Save className="size-4" />
                    Save draft
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void openConfirmation('schedule')}
                  >
                    <CalendarClock className="size-4" />
                    Schedule
                  </Button>
                  <Button type="button" onClick={() => void openConfirmation('send')}>
                    <Send className="size-4" />
                    Send now
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader className="space-y-1 pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Eye className="text-muted-foreground size-4" />
                Preview
              </CardTitle>
              <CardDescription className="text-xs">
                Rendered from the current HTML draft. Scripts are sandboxed.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-muted/20 overflow-hidden rounded-md border">
                <div className="border-b px-3 py-2">
                  <p className="truncate text-sm font-medium">{draft.subject || 'No subject'}</p>
                  <p className="text-muted-foreground truncate text-xs">
                    {draft.previewText || 'No preview text'}
                  </p>
                </div>
                <iframe
                  title="Marketing email preview"
                  sandbox=""
                  srcDoc={previewDoc}
                  className="h-[480px] w-full bg-white"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="space-y-1 pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <ImageIcon className="text-muted-foreground size-4" />
                Campaign images
              </CardTitle>
              <CardDescription className="text-xs">
                Upload public email images and insert them into the HTML draft.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <input
                ref={assetInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  if (file) void uploadMarketingAsset(file)
                }}
              />
              <div className={compactActionGridClass}>
                <Input
                  value={assetAltText}
                  onChange={(event) => setAssetAltText(event.target.value)}
                  placeholder="Alt text for the next image"
                />
                <Button
                  type="button"
                  variant="outline"
                  loading={isUploadingAsset}
                  onClick={() => assetInputRef.current?.click()}
                >
                  <Upload className="size-4" />
                  Upload
                </Button>
              </div>

              <div className="max-h-[260px] space-y-2 overflow-y-auto pr-1">
                {isAssetsLoading ? (
                  <div className="text-muted-foreground rounded-md border px-3 py-2 text-sm">
                    Loading images...
                  </div>
                ) : assets.length === 0 ? (
                  <div className="text-muted-foreground rounded-md border border-dashed px-3 py-2 text-sm">
                    No campaign images yet.
                  </div>
                ) : (
                  assets.map((asset) => (
                    <div
                      key={asset.id}
                      className="grid grid-cols-[52px_1fr] items-center gap-3 rounded-md border p-2 sm:grid-cols-[64px_1fr_auto]"
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={asset.publicUrl}
                        alt={asset.altText || 'Campaign asset'}
                        className="size-12 rounded-md object-cover sm:size-16"
                      />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">
                          {asset.altText || 'Campaign image'}
                        </p>
                        <p className="text-muted-foreground truncate text-xs">
                          {asset.contentType} {formatBytes(asset.sizeBytes)}
                        </p>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="col-span-2 sm:col-span-1"
                        onClick={() => insertAssetIntoHtml(asset)}
                      >
                        <Plus className="size-4" />
                        Insert
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="space-y-1 pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <ListFilter className="text-muted-foreground size-4" />
                Losi users and segment
              </CardTitle>
              <CardDescription className="text-xs">
                Sync Supabase profile users to Resend, then send to that segment.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {resendSegments.length > 0 ? (
                <Select
                  value={draft.resendSegmentId || undefined}
                  onValueChange={(value) => updateDraft('resendSegmentId', value)}
                >
                  <SelectTrigger aria-label="Target Resend segment">
                    <SelectValue placeholder="Select existing Resend segment" />
                  </SelectTrigger>
                  <SelectContent>
                    {resendSegments.map((segment) => (
                      <SelectItem key={segment.id} value={segment.id}>
                        {getResendSegmentLabel(segment)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}

              <div className={compactActionGridClass}>
                <div className="text-muted-foreground rounded-md border px-3 py-2 text-xs">
                  Audience source:{' '}
                  <span className="text-foreground font-medium">All Losi profiles with email</span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  loading={isSyncingUsers}
                  onClick={syncLosiUsersToResend}
                >
                  <RefreshCw className="size-4" />
                  Sync users
                </Button>
              </div>

              <div className="text-muted-foreground rounded-md border px-3 py-2 text-xs">
                Current Resend segment:{' '}
                <span className="text-foreground font-mono">
                  {selectedResendSegment
                    ? getResendSegmentLabel(selectedResendSegment)
                    : draft.resendSegmentId || 'not set'}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="space-y-1 pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <ShieldCheck className="text-muted-foreground size-4" />
                Access sharing
              </CardTitle>
              <CardDescription className="text-xs">
                Owners and admins can grant campaign permissions to workspace users.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!draft.id ? (
                <div className="border-border/70 bg-muted/20 rounded-md border border-dashed p-3 text-sm">
                  Save this campaign before sharing access.
                </div>
              ) : (
                <>
                  <div className={accessGrantGridClass}>
                    <Select value={grantUserId} onValueChange={setGrantUserId}>
                      <SelectTrigger aria-label="Workspace user">
                        <SelectValue
                          placeholder={
                            isAccessLoading ? 'Loading users...' : 'Select workspace user'
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {availableMembers.map((member) => (
                          <SelectItem key={member.userId} value={member.userId}>
                            {getMemberLabel(member)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={grantPermission}
                      onValueChange={(value) => setGrantPermission(readPermission(value))}
                    >
                      <SelectTrigger aria-label="Permission">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PERMISSION_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button onClick={grantAccess} loading={isGranting}>
                      <UserPlus className="size-4" />
                      Grant
                    </Button>
                  </div>

                  {accessError ? (
                    <p className="text-muted-foreground rounded-md border px-3 py-2 text-xs">
                      Sharing data unavailable: {accessError}
                    </p>
                  ) : null}

                  <div className="divide-border/70 rounded-md border">
                    {grants.length === 0 ? (
                      <p className="text-muted-foreground p-3 text-sm">
                        No explicit campaign grants yet.
                      </p>
                    ) : (
                      grants.map((grant) => (
                        <div
                          key={grant.userId}
                          className="flex items-center justify-between gap-3 border-b px-3 py-2 last:border-b-0"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">{getMemberLabel(grant)}</p>
                            <p className="text-muted-foreground truncate text-xs">
                              {grant.role || 'workspace user'}
                            </p>
                          </div>
                          <Badge variant="outline" className="shrink-0">
                            {getPermissionLabel(grant.permission)}
                          </Badge>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {showSidekick ? (
        <CampaignSidekickBubble
          instruction={sidekickInstruction}
          isOpen={isSidekickOpen}
          isWorking={isAiWorking}
          mode={sidekickMode}
          onInstructionChange={setSidekickInstruction}
          onModeChange={setSidekickMode}
          onOpenChange={setIsSidekickOpen}
          onRun={(mode) => void runCampaignAi(mode)}
        />
      ) : null}

      <AlertDialog
        open={Boolean(confirmAction)}
        onOpenChange={(open) => !open && setConfirmAction(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmCopy?.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmCopy?.description} This campaign targets{' '}
              {draft.resendSegmentId
                ? `segment ${draft.resendSegmentId}`
                : draft.resendAudienceId
                  ? `audience ${draft.resendAudienceId}`
                  : draft.resendTopicId
                    ? `topic ${draft.resendTopicId}`
                    : 'the configured Resend recipients'}
              {confirmAction === 'schedule'
                ? ` at ${formatDate(toIsoOrNull(draft.scheduledAt))}.`
                : '.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(actionLoading)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={Boolean(actionLoading)}
              onClick={(event) => {
                event.preventDefault()
                void runConfirmedAction()
              }}
            >
              {actionLoading ? (
                <RefreshCw className="size-4 animate-spin" />
              ) : (
                <CheckCircle2 className="size-4" />
              )}
              {confirmCopy?.button}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// --------------

function CampaignSidekickBubble({
  instruction,
  isOpen,
  isWorking,
  mode,
  onInstructionChange,
  onModeChange,
  onOpenChange,
  onRun
}: {
  instruction: string
  isOpen: boolean
  isWorking: boolean
  mode: AiMode
  onInstructionChange: (value: string) => void
  onModeChange: (mode: AiMode) => void
  onOpenChange: (open: boolean) => void
  onRun: (mode: AiMode) => void
}) {
  if (!isOpen) {
    return (
      <Button
        type="button"
        size="icon"
        className="fixed bottom-6 right-6 z-[100] size-12 rounded-full shadow-lg"
        aria-label="Open campaign Sidekick"
        onClick={() => onOpenChange(true)}
      >
        <MessageCircle className="size-5" />
      </Button>
    )
  }

  return (
    <div className="bg-background fixed bottom-6 right-6 z-[100] w-[min(420px,calc(100vw-2rem))] rounded-lg border shadow-xl">
      <div className="flex items-center justify-between gap-3 border-b px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Bot className="text-primary size-4" />
            <p className="truncate text-sm font-semibold">Campaign Sidekick</p>
          </div>
          <p className="text-muted-foreground truncate text-xs">
            Draft, edit, and tighten this email with Copy Azure.
          </p>
        </div>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label="Close campaign Sidekick"
          onClick={() => onOpenChange(false)}
        >
          <X className="size-4" />
        </Button>
      </div>

      <div className="space-y-3 p-4">
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'Draft', value: 'draft' as const, icon: Sparkles },
            { label: 'Edit', value: 'edit' as const, icon: Wand2 },
            { label: 'Subject', value: 'subject' as const, icon: Mail }
          ].map((item) => {
            const Icon = item.icon

            return (
              <Button
                key={item.value}
                type="button"
                size="sm"
                variant={mode === item.value ? 'default' : 'outline'}
                onClick={() => onModeChange(item.value)}
              >
                <Icon className="size-4" />
                {item.label}
              </Button>
            )
          })}
        </div>

        <Textarea
          value={instruction}
          onChange={(event) => onInstructionChange(event.target.value)}
          className="min-h-[120px]"
          placeholder="Make this warmer, add the uploaded image, and aim it at dormant users."
        />

        <div className="grid grid-cols-2 gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={isWorking}
            onClick={() => {
              onModeChange('rewrite')
              onRun('rewrite')
            }}
          >
            <Wand2 className="size-4" />
            Rewrite
          </Button>
          <Button type="button" loading={isWorking} onClick={() => onRun(mode)}>
            <Sparkles className="size-4" />
            Apply edit
          </Button>
        </div>
      </div>
    </div>
  )
}

// --------------

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-background rounded-md border px-3 py-2">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="font-mono text-xl font-semibold">{value}</p>
    </div>
  )
}

// --------------

function Field({
  children,
  htmlFor,
  label
}: {
  children: ReactNode
  htmlFor: string
  label: string
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  )
}
